import { useTranslation } from 'react-i18next'
import { MenuItem, TextField } from '@mui/material'
import { forwardRef, useImperativeHandle, useState } from 'react'

import { customersDocumentType } from '../../lib'
import { textFieldWhiteBg } from '../../theme/custom-styles'
import { CompanyOnboardingData } from './add-new-customer-company-modal'

export const NewCustomerCompanyFormStepOne = forwardRef(({ errors }: { errors: { [key: string]: string } }, ref) => {
  const { t } = useTranslation(['customer-companies-management'])

  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const [companyOnboardingData, setCompanyOnboardingData] = useState<Partial<CompanyOnboardingData>>({})

  useImperativeHandle(
    ref,
    () => {
      return {
        companyOnboardingData,
        handleSetCompanyOnboardingData: (companyOnboardingDataToSet: CompanyOnboardingData, isUpdate?: boolean) => {
          setCompanyOnboardingData(companyOnboardingDataToSet)

          if (isUpdate) {
            setIsUpdating(isUpdate)
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyOnboardingData]
  )

  return (
    <div
      className="form-container"
      style={{ marginTop: '0px' }}
    >
      <div className="form-main-instruction">{t('newClientForm.formStepOne.mainInstruction')}</div>
      <div className="form-fields-container">
        <TextField
          fullWidth
          variant="standard"
          sx={textFieldWhiteBg}
          error={!!errors.businessName}
          helperText={errors.businessName}
          value={companyOnboardingData?.businessName ?? ''}
          label={t('newClientForm.formStepThree.businessName')}
          onChange={(event) => {
            setCompanyOnboardingData((currentCompanyOnboardingData) => {
              return {
                ...currentCompanyOnboardingData,
                businessName: event.target.value.toUpperCase()
              }
            })
          }}
        />
        <div className="form-fields-row">
          <TextField
            select
            fullWidth
            variant="standard"
            error={!!errors.documentType}
            helperText={errors.documentType}
            value={companyOnboardingData.documentType ?? ''}
            label={t('newClientForm.formStepOne.businessOwnerDocumentType')}
            onChange={(event) => {
              setCompanyOnboardingData((currentBusinessOwnerData: Partial<CompanyOnboardingData>) => {
                return {
                  ...currentBusinessOwnerData,
                  documentType: event.target.value
                }
              })
            }}
            sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.2em', display: 'flex', alignItems: 'center' } }}
          >
            <MenuItem
              disabled
              value=""
            >
              {t('newClientForm.formStepOne.documentTypeOptions.select')}
            </MenuItem>
            {Object.keys(customersDocumentType).map((documentType: string) => {
              return (
                <MenuItem
                  key={documentType}
                  value={documentType}
                >
                  {(customersDocumentType as any)[documentType].toUpperCase()}
                </MenuItem>
              )
            })}
          </TextField>
          <TextField
            fullWidth
            variant="standard"
            sx={textFieldWhiteBg}
            error={!!errors.documentNumber}
            helperText={errors.documentNumber}
            value={companyOnboardingData.documentNumber ?? ''}
            label={t('newClientForm.formStepOne.businessOwnerDocumentNumber')}
            onChange={(event) => {
              setCompanyOnboardingData((currentBusinessOwnerData: Partial<CompanyOnboardingData>) => {
                return {
                  ...currentBusinessOwnerData,
                  documentNumber: event.target.value.toUpperCase()
                }
              })
            }}
          />
        </div>
        <div className="form-fields-row">
          <TextField
            fullWidth
            variant="standard"
            sx={textFieldWhiteBg}
            disabled={isUpdating}
            error={!!errors.companyMainUserDataEmail}
            helperText={errors.companyMainUserDataEmail}
            label={t('newClientForm.formStepOne.businessEmail')}
            value={companyOnboardingData.companyMainUserData?.email ?? ''}
            onChange={(event) => {
              setCompanyOnboardingData((currentBusinessOwnerData: Partial<CompanyOnboardingData>) => {
                const currentCompanyMainUserData = { ...companyOnboardingData.companyMainUserData }

                return {
                  ...currentBusinessOwnerData,
                  companyMainUserData: {
                    ...currentCompanyMainUserData,
                    email: event.target.value.toLowerCase()
                  }
                }
              })
            }}
          />
          <TextField
            select
            fullWidth
            variant="standard"
            error={!!errors.language}
            helperText={errors.language}
            value={companyOnboardingData.language ?? ''}
            label={t('newClientForm.formStepOne.language')}
            onChange={(event) => {
              setCompanyOnboardingData((currentCompanyOnboardingData: Partial<CompanyOnboardingData>) => {
                return {
                  ...currentCompanyOnboardingData,
                  language: event.target.value
                }
              })
            }}
            sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.2em', display: 'flex', alignItems: 'center' } }}
          >
            <MenuItem
              disabled
              value=""
            >
              {t('newClientForm.formStepOne.languageOptions.select')}
            </MenuItem>
            <MenuItem value="en">{t('newClientForm.formStepOne.languageOptions.english')}</MenuItem>
            <MenuItem value="es">{t('newClientForm.formStepOne.languageOptions.spanish')}</MenuItem>
          </TextField>
        </div>
        <div className="form-fields-row">
          <TextField
            fullWidth
            variant="standard"
            sx={textFieldWhiteBg}
            error={!!errors.companyMainUserDataFirstName}
            helperText={errors.companyMainUserDataFirstName}
            value={companyOnboardingData.companyMainUserData?.firstName ?? ''}
            label={t('newClientForm.formStepOne.businessOwnerFirstName')}
            onChange={(event) => {
              setCompanyOnboardingData((currentBusinessOwnerData: Partial<CompanyOnboardingData>) => {
                const currentCompanyMainUserData = { ...companyOnboardingData.companyMainUserData }

                return {
                  ...currentBusinessOwnerData,
                  companyMainUserData: {
                    ...currentCompanyMainUserData,
                    firstName: event.target.value.toUpperCase()
                  }
                }
              })
            }}
          />
          <TextField
            fullWidth
            variant="standard"
            sx={textFieldWhiteBg}
            error={!!errors.companyMainUserDataLastName}
            helperText={errors.companyMainUserDataLastName}
            value={companyOnboardingData.companyMainUserData?.lastName ?? ''}
            label={t('newClientForm.formStepOne.businessOwnerLastName')}
            onChange={(event) => {
              setCompanyOnboardingData((currentBusinessOwnerData: Partial<CompanyOnboardingData>) => {
                const currentCompanyMainUserData = { ...companyOnboardingData.companyMainUserData }

                return {
                  ...currentBusinessOwnerData,
                  companyMainUserData: {
                    ...currentCompanyMainUserData,
                    lastName: event.target.value.toUpperCase()
                  }
                }
              })
            }}
          />
        </div>
      </div>
    </div>
  )
})
